<template>
  <validation-observer
    v-if="shouldQuestionRender && isRenderAfterFullfilled"
    v-slot="observer"
    slim
  >
    <form-section
      :id="questionId || 'address'"
      :validated="formObserver.validated && observer.failed"
      v-bind="observer"
      :class="{ 'columns-align': isColumnLayout }"
    >
      <template v-if="!postcodeAddressModel">
        <!-- POSTCODE -->
        <form-row
          :label="title || `What's your postcode?`"
          :help-text="helpText"
          :error="formObserver.validated && observer.failed"
          :is-vertical-splitted="isColumnLayout"
        >
          <validation-provider
            v-slot="{ failed }"
            name="Postcode"
            :vid="`${stepId}_${kebabCase(questionFields.postcode)}`"
            :rules="postcodeRules"
            slim
          >
            <div class="d-flex postcode-search-wrap">
              <div class="flex-grow-1 mr-3 mr-sm-6">
                <qz-text-field
                  v-model.trim="questionData[questionFields.postcode]"
                  :data-test="fieldTrackerId.postcode"
                  :placeholder="postcodePlaceholder"
                  :error="failed"
                  :maxlength="inputCharsLimiters.POSTCODE_MAX"
                  autocomplete="postal-code"
                  @keyup="helpers.limitInputChars(stepData, questionFields.postcode, inputCharsLimiters.POSTCODE_MAX)"
                  @input="onPostcodeChange"
                  @keyup.enter="!failed && searchAddress(questionData[questionFields.postcode])"
                />
              </div>
              <div>
                <qz-btn
                  rounded
                  color="navy-light"
                  :loading="postcode.loading"
                  :disabled="postcode.loading"
                  light
                  class="btn btn-find-address"
                  data-test="find_address_btn"
                  style="min-width: 169px; height: 100%;"
                  @click.prevent="!failed && searchAddress(questionData[questionFields.postcode])"
                >
                  Find address
                  <v-icon
                    dark
                    right
                    size="22"
                  >
                    mdi-magnify
                  </v-icon>
                  <template #loader>
                    <span class="custom-loader">
                      <v-icon light>mdi-cached</v-icon>
                    </span>
                  </template>
                </qz-btn>
              </div>
            </div>
          </validation-provider>
          <validation-provider
            v-slot="{ failed }"
            :vid="`${stepId}_${questionId}`"
            name="Address"
            rules="required"
            tag="div"
          >
            <input
              :value="isAddressValid"
              type="hidden"
            >
            <div class="error--text mt-4 fz12">
              <template
                v-if="
                  failed &&
                    formObserver.validated &&
                    !postcode.errors.length &&
                    !observer.fields[`${stepId}_${kebabCase(questionFields.postcode)}`].valid
                "
              >
                Please remember to select the address.
              </template>
              <template
                v-else-if="
                  observer.fields[`${stepId}_${kebabCase(questionFields.postcode)}`] &&
                    observer.fields[`${stepId}_${kebabCase(questionFields.postcode)}`].failed
                "
              >
                {{ observer.errors[`${stepId}_${kebabCase(questionFields.postcode)}`][0] }}
              </template>
              <template v-else-if="postcode.errors.length > 0">
                <template v-for="error in postcode.errors">
                  {{ error }}
                </template>
              </template>
            </div>
          </validation-provider>
        </form-row>
        <!-- ADDRESS -->
        <form-row
          v-if="questionData[questionFields.postcode] && postcode.addresses.length"
          question="Please select the address"
          :is-vertical-splitted="isColumnLayout"
        >
          <validation-provider
            v-slot="{ errors }"
            :vid="`${stepId}_address`"
            name="Address"
            :rules="addressRules"
          >
            <qz-select
              v-model="postcodeAddressModel"
              :data-test="fieldTrackerId.address"
              :items="postcode.addresses"
              :error-messages="errors"
              :placeholder="helpers.getPlaceholderResponsiveText('Please select address', isMobile)"
              return-object
            />
          </validation-provider>
          <checkbox-square
            v-if="addNotListedButton"
            :checked="addressIsNotListed"
            class="mt-4"
            :name="`${stepId}.${questionId}`"
            title="My address is not listed"
            @change="handleNotListedClick(observer)"
          />
        </form-row>
      </template>
      <!-- SUMMARY -->
      <form-row
        v-else
        :label="subtitle || 'Your address'"
        :is-vertical-splitted="false"
      >
        <box
          id="postcode-summary"
          class="postcode-summary"
        >
          <div class="d-flex align-center">
            <div
              class="item-icon"
              v-html="IconHome"
            />
            {{ postcodeAddressModel.text }}, {{ postcodeAddressModel.postcode }}
          </div>
          <a
            href="#"
            class="link-box"
            data-test="change_my_address_btn"
            @click.prevent="clearSelectedAddress"
          >
            <span>Change address</span>
            <v-icon color="primary">mdi-pencil</v-icon>
          </a>
        </box>
      </form-row>
    </form-section>
  </validation-observer>
</template>

<script>
import IconHome from '!svg-inline-loader!@/assets/icons/icon-home.svg';
import QuestionBase from './Base';
import {
  isEmpty,
  isObject,
  snakeCase,
  kebabCase,
} from 'lodash';

export default {
  name: 'question-address',
  extends: QuestionBase,
  props: {
    addNotListedButton: {
      type: Boolean,
      default: false,
    },
    /**
     * Used in postcodeContact question to disable
     * postcode "startsWith validation rule part"
     * as contact postcode could start with any letters
     */
    hasStartsWithValidation: {
      type: Boolean,
      default: true,
    },
  },
  data () {
    return {
      IconHome,
      fields: [
        'postcode',
        'houseNumberOrName',
        'addressLine2',
        'addressLine3',
        'addressLine4',
      ],
      postcode: {
        addresses: [],
        errors: [],
        loading: false,
      },
      addressIsNotListed: false,
    };
  },
  computed: {
    postcodePlaceholder () {
      return this.helpers.getPostcodeExample(this.config.customValidations?.postcode?.startsWith);
    },
    isAddressValid () {
      return this.questionData[this.questionFields.postcode] &&
        this.questionData[this.questionFields.houseNumberOrName];
    },
    postcodeAddressModel: {
      get () {
        if (this.postcode.addresses.length === 0) return null;

        const houseNumberOrName = this.questionData[this.questionFields.houseNumberOrName];

        const selectedAddress = this.postcode.addresses.find(({ raw }) => {
          return houseNumberOrName === raw.address_line;
        });

        return selectedAddress || null;
      },
      set (val) {
        this.questionData[this.questionFields.houseNumberOrName] = val.raw.address_line;
        this.questionData[this.questionFields.addressLine2] = val.raw.address_line2;
        this.questionData[this.questionFields.addressLine3] = val.raw.address_line3;
        this.questionData[this.questionFields.addressLine4] = val.raw.address_line4;
      },
    },
    postcodeRules () {
      if (!this.config.customValidations?.postcode) return 'postcode';
      return {
        postcode:
          {
            ...this.config.customValidations.postcode,
            hasStartsWithValidation: this.hasStartsWithValidation,
          },
      };
    },
    postcodeField () {
      return this.questionData[this.questionFields.postcode];
    },
    addressRules () {
      return this.addressIsNotListed ? '' : 'listOptionRequired';
    },
    fieldTrackerId () {
      return {
        postcode: snakeCase(this.questionFields.postcode),
        address: `${snakeCase(this.questionFields.postcode)}_address`,
      };
    },
  },
  watch: {
    async postcodeField (val, prevVal) {
      // If the user changes the postcode reset the addressIsNotListed field
      if (prevVal !== undefined && this.addNotListedButton) {
        this.addressIsNotListed = false;
      }

      if (val && prevVal === undefined) {
        await this.searchAddress(val);
      }
    },
  },
  async created () {
    // Set button check on form reload if the manual address exists in the sessionData
    if (this.addNotListedButton) {
      this.addressIsNotListed = this.questionData[this.questionFields.houseNumberOrName] === 'Not listed';
    }

    const postcode = this.questionData[this.questionFields.postcode];

    if (postcode) {
      await this.searchAddress(postcode);
    }
  },
  methods: {
    kebabCase,
    handleNotListedClick (observer) {
      if (this.addressIsNotListed) {
        this.addressIsNotListed = false;
        this.clearSelectedAddress();
        return;
      }

      this.addressIsNotListed = true;
      this.questionData[this.questionFields.houseNumberOrName] = 'Not listed';

      // Reset the address observer, so it has to revalidate the question.
      observer.reset();
    },
    onPostcodeChange () {
      this.questionData[this.questionFields.postcode] = this.questionData[this.questionFields.postcode].toUpperCase();
      [
        this.questionFields.houseNumberOrName,
        this.questionFields.addressLine2,
        this.questionFields.addressLine3,
        this.questionFields.addressLine4,
      ].forEach(field => {
        this.questionData[field] = null;
      });
      this.postcode.addresses = [];
      this.postcode.errors = [];
    },
    async searchAddress (query) {
      if (!query) {
        this.$emit('toast', {
          type: 'info',
          text: 'Please, type postcode to perform an address search.',
        });
        return false;
      }

      this.postcode.loading = true;
      this.postcode.errors = [];

      try {
        const { data, status } = await this.$api.searchFullPostcode(query);
        const errorMsg = `No addresses found for this postcode (${query}).`;
        this.postcode.errors = [];

        if (
          status === 'success' &&
          data.length > 0 &&
          typeof data[0] !== 'string'
        ) {
          // [SU-4513] looks like in some cases we receive an empty object, so skip empty objects before mapping
          this.postcode.addresses = data.reduce((acc, address) => {
            if (isObject(address) && !isEmpty(address)) {
              return [
                ...acc,
                {
                  raw: address,
                  value: `${address.postCode}-${address.address_line}`,
                  text: address.address_line,
                  postcode: `${address.postCodePrefix} ${address.postCodeSufix}`,
                },
              ];
            }
            return acc;
          }, []);
        } else {
          this.postcode.errors.push(errorMsg);
        }
      } catch (error) {
        this.postcode.errors = [];
        this.postcode.errors.push(error.toString());
      }
      this.postcode.loading = false;
    },
    clearSelectedAddress () {
      this.questionData[this.questionFields.houseNumberOrName] = null;
      this.questionData[this.questionFields.addressLine2] = null;
      this.questionData[this.questionFields.addressLine3] = null;
      this.questionData[this.questionFields.addressLine4] = null;

      if (this.addNotListedButton) {
        this.addressIsNotListed = false;
      }
    },
  },
};
</script>

<style lang="scss">
.postcode-search-wrap {
  @media screen and (max-width: 459px) {
    flex-direction: column;
  }

  button {
    @media screen and (max-width: 459px) {
      width: 100%;
      min-height: 56px;
      margin-top: 16px;
    }
  }
}

.postcode-summary {
  &.box {
    padding: 24px 16px;
  }

  .box__content {
    display: flex;
    align-items: center;
    justify-content: space-between;

    @media screen and (max-width: 599px) {
      flex-wrap: wrap;
      justify-content: space-between;
      font-size: 14px;

      .item-icon {
        width: 28px;
        height: 28px;
        margin-right: 10px;
      }

      .link-box {
        display: flex;
        align-items: center;
        height: 36px;
        margin-left: auto;
      }
    }
  }

  .box__actions {
    display: none;
  }

  .link-box {
    margin-left: 10px;
    white-space: nowrap;
    font-size: 14px;
  }
}

.item-icon {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  margin-right: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--v-primary-base);
  font-size: 0;
  line-height: 0;
  position: relative;

  svg {
    width: 56px;
    height: 56px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    border-radius: 50%;
    background-color: currentColor;
    opacity: 0.2;
  }
}
</style>

<style lang="scss">
// fix postcode input on 2 columns view
.columns-align .form-row {
  @media screen and (max-width: 959px) {
    flex-direction: column;
  }

  .form-col {
    @media screen and (max-width: 959px) {
      width: 100% !important;

      & + .form-col {
        margin-top: 23px !important;
      }
    }
  }
}
</style>
